import { InfiniteData, QueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';
import { FilterValues } from '@/hooks/use-filter-values';
import { DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS } from '@/lib/constants/pagination';
import {
  HORIZONTAL_PAGINATION_THRESHOLD_DAYS,
  MAX_CACHED_DATE_RANGE_MONTHS,
  PLANNER_PAGINATION_LOG_TAG,
  QUARTER_IN_MONTHS,
} from '@/modules/planner';
import { logger } from '@/utils/logger/logger';

import { mapUtilizationBasedOnToDTO, mapUtilizationFiltersToDTO } from '../../mappers';
import { mapAssignmentFilters } from '../../mappers/map-assignment-filters';
import usePlannerStore from '../../store';
import {
  AssignmentActivePeriodResponse,
  DailyUtilizationResponse,
  PlannerAssignmentResponse,
  PlannerMemberResponse,
  PlannerUserStateChangeResponse,
} from '../../types/dto';
import { PlannerTimeoffResponse } from '../../types/dto/response/planner-timeoff-response';
import { PlannerDateRangeDirection } from '../../types/planner-date-range-direction';
import { PlannerPreferences } from '../../types/utilization-filters';

/**
 * Page parameter for dual-axis pagination.
 * Combines date range (horizontal) with cursor (vertical) pagination.
 */
export interface DualAxisPageParam {
  startDate: string;
  endDate: string;
  cursor?: number | null;
  direction?: PlannerDateRangeDirection;
  axis: 'horizontal' | 'vertical';
}

/**
 * Extended response with metadata for tracking pagination state.
 */
export interface PageResponseWithMetadata<T> {
  items: T[];
  pagination?: {
    total?: number;
    cursor?: number | null;
  };
  _dateRange: { startDate: string; endDate: string };
  _cursor: number | null | undefined;
}

interface FetchDateRangeParams<TApiResponse> {
  queryClient: QueryClient;
  queryKey: readonly unknown[];
  filters: FilterValues['values'];
  plannerPreferences: PlannerPreferences;
  apiFn: (params: any) => Promise<TApiResponse>;
  /**
   * Returns the "weight" of an item for page size calculation.
   * For member grouping: each item is 1 member → returns 1 (default).
   * For project/department grouping: each item is a group containing N members →
   * returns N so the horizontal extension pageSize matches the backend's member-weighted pagination.
   */
  getItemWeight?: (item: any) => number;
}

interface DateRangeCalculation {
  startDate: Dayjs;
  endDate: Dayjs;
}

// ============================================================================
// MERGE UTILITIES
// ============================================================================

/**
 * Merges assignments from different date ranges, combining activePeriods
 * and other fields for assignments with the same ID.
 *
 * This handles the case where a long assignment spans multiple date ranges
 * and needs to accumulate activePeriods across horizontal pagination.
 */
export function mergeAssignments(
  existingAssignments: PlannerAssignmentResponse[],
  newAssignments: PlannerAssignmentResponse[]
): void {
  const existingAssignmentsMap = new Map(existingAssignments.map(a => [a.assignment.id, a]));

  newAssignments.forEach(newAssignment => {
    const existingAssignment = existingAssignmentsMap.get(newAssignment.assignment.id);

    if (existingAssignment) {
      mergeExistingAssignment(existingAssignment, newAssignment);
    } else {
      // Deep copy to avoid mutating TanStack Query cached data on subsequent
      // select() re-runs when mergeExistingAssignment pushes into activePeriods/overlapsAssignmentIds
      const copy = {
        ...newAssignment,
        activePeriods: [...newAssignment.activePeriods],
        overlapsAssignmentIds: [...newAssignment.overlapsAssignmentIds],
        adjustedMinutesByDate: newAssignment.adjustedMinutesByDate
          ? { ...newAssignment.adjustedMinutesByDate }
          : undefined,
      };
      existingAssignments.push(copy);
      existingAssignmentsMap.set(copy.assignment.id, copy);
    }
  });
}

/**
 * Merges time-off requests across date range pages, deduplicating by ID
 * and combining days from overlapping pages.
 */
export function mergeTimeOffs(
  existingTimeOffs: PlannerTimeoffResponse[],
  newTimeOffs: PlannerTimeoffResponse[]
): void {
  const existingMap = new Map(existingTimeOffs.map(t => [t.id, t]));

  newTimeOffs.forEach(newTimeOff => {
    const existing = existingMap.get(newTimeOff.id);

    if (existing) {
      const existingDates = new Set(existing.days.map(d => d.date));
      newTimeOff.days.forEach(day => {
        if (!existingDates.has(day.date)) {
          existing.days.push(day);
        }
      });
      existing.days.sort((a, b) => a.date.localeCompare(b.date));

      // Extend the span to cover all merged days. The backend clips
      // startDate/endDate to each page's queried range, so a long time-off
      // would otherwise keep the first page's narrow range and only render
      // across that page. Keep startDate/endDate in sync with the merged days
      // so downstream consumers (and the days-derived mapper) agree.
      if (existing.days.length > 0) {
        existing.startDate = existing.days[0].date;
        existing.endDate = existing.days[existing.days.length - 1].date;
      }
    } else {
      const copy = { ...newTimeOff, days: [...newTimeOff.days] };
      existingTimeOffs.push(copy);
      existingMap.set(copy.id, copy);
    }
  });
}

/**
 * Deep copies a PlannerMemberResponse, cloning mutable arrays to prevent
 * select() from mutating TanStack Query's cached data across re-runs.
 * Used when inserting a new member into a merged map for the first time.
 */
export function deepCopyMember(member: PlannerMemberResponse): PlannerMemberResponse {
  return {
    ...member,
    assignments: member.assignments.map(a => ({
      ...a,
      activePeriods: [...a.activePeriods],
      overlapsAssignmentIds: [...a.overlapsAssignmentIds],
      // Clone the per-date adjustment map so cross-page merges don't mutate
      // TanStack Query's cached page data on subsequent select() re-runs.
      adjustedMinutesByDate: a.adjustedMinutesByDate ? { ...a.adjustedMinutesByDate } : undefined,
    })),
    timeOffs: member.timeOffs.map(t => ({ ...t, days: [...t.days] })),
    stateChanges: [...member.stateChanges],
    utilizationByDate: [...member.utilizationByDate],
  };
}

/**
 * Merges properties of an existing assignment with a new one from a different date range.
 */
function mergeExistingAssignment(
  existingAssignment: PlannerAssignmentResponse,
  newAssignment: PlannerAssignmentResponse
): void {
  // Merge activePeriods
  const existingPeriods = new Set(
    existingAssignment.activePeriods.map(p => `${p.dateRange.startDate}-${p.dateRange.endDate}`)
  );
  const uniqueNewPeriods = newAssignment.activePeriods.filter(
    p => !existingPeriods.has(`${p.dateRange.startDate}-${p.dateRange.endDate}`)
  );
  existingAssignment.activePeriods.push(...uniqueNewPeriods);

  // For continuous assignments (doSkipWeekdays=false), merge consecutive active periods
  // to prevent visual "cuts" when periods are split by date range boundaries
  if (!existingAssignment.assignment.doSkipWeekdays) {
    existingAssignment.activePeriods = mergeConsecutiveActivePeriods(
      existingAssignment.activePeriods
    );
  }

  // Merge overlapsAssignmentIds
  const existingOverlapIds = new Set(existingAssignment.overlapsAssignmentIds);
  const uniqueNewOverlapIds = newAssignment.overlapsAssignmentIds.filter(
    id => !existingOverlapIds.has(id)
  );
  existingAssignment.overlapsAssignmentIds.push(...uniqueNewOverlapIds);

  // Update flags
  existingAssignment.overlapsSameProject =
    existingAssignment.overlapsSameProject || newAssignment.overlapsSameProject;
  existingAssignment.isFilteredOut =
    existingAssignment.isFilteredOut && newAssignment.isFilteredOut;
  existingAssignment.belongsToTheGroup =
    existingAssignment.belongsToTheGroup || newAssignment.belongsToTheGroup;

  // Merge per-date adjusted minutes (reduced minutes on days overlapping a
  // time-off). This is page-scoped in the backend response: a page covering
  // the time-off days carries the reduced values, a page without them omits
  // them. If we don't union these across pages, an assignment first inserted
  // from a page WITHOUT the adjustment keeps its full height and visually
  // overlaps the time-off block until a re-fetch/mutation recomputes it.
  if (newAssignment.adjustedMinutesByDate) {
    existingAssignment.adjustedMinutesByDate = {
      ...existingAssignment.adjustedMinutesByDate,
      ...newAssignment.adjustedMinutesByDate,
    };
  }

  // Fill only an unknown (optimistic `null`) total: the post-mutation refresh merges the fresh page
  // first, so a later preserved page must never overwrite it
  if (existingAssignment.bookedMinutes === null) {
    existingAssignment.bookedMinutes = newAssignment.bookedMinutes;
  }
}

/**
 * Checks if two assignment rates are equal.
 * Both null → equal; one null → not equal; otherwise compare all fields.
 */
function areRatesEqual(
  a: AssignmentActivePeriodResponse['rate'],
  b: AssignmentActivePeriodResponse['rate']
): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;

  return (
    a.rate === b.rate &&
    a.currency.code === b.currency.code &&
    a.assignmentRateSource === b.assignmentRateSource &&
    a.projectBoundRateSource === b.projectBoundRateSource
  );
}

/**
 * Merges consecutive active periods into continuous ranges when they are
 * date-consecutive AND have the same rate. Periods with different rates
 * are kept separate even if dates are consecutive.
 *
 * This is used for assignments with doSkipWeekdays=false to prevent visual fragmentation
 * when periods are split across date range boundaries.
 *
 * Example (same rate):
 * Input:  [{dateRange: {start: "2026-02-14", end: "2026-08-15"}, rate: R}, {dateRange: {start: "2026-08-16", end: "2026-11-15"}, rate: R}]
 * Output: [{dateRange: {start: "2026-02-14", end: "2026-11-15"}, rate: R}]
 *
 * Example (different rates):
 * Input:  [{dateRange: {start: "2026-02-14", end: "2026-08-15"}, rate: R1}, {dateRange: {start: "2026-08-16", end: "2026-11-15"}, rate: R2}]
 * Output: (unchanged — two separate periods)
 */
function mergeConsecutiveActivePeriods(
  periods: AssignmentActivePeriodResponse[]
): AssignmentActivePeriodResponse[] {
  // Filter out periods with null endDate before merging
  const validPeriods = periods.filter(p => p.dateRange.endDate !== null);
  if (validPeriods.length <= 1) return validPeriods;

  // Sort periods by startDate
  const sorted = [...validPeriods].sort((a, b) => {
    return new Date(a.dateRange.startDate).getTime() - new Date(b.dateRange.startDate).getTime();
  });

  const merged: AssignmentActivePeriodResponse[] = [];
  let current: AssignmentActivePeriodResponse = {
    ...sorted[0],
    dateRange: { ...sorted[0].dateRange },
  };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    const currentEnd = dayjs(current.dateRange.endDate);
    const nextStart = dayjs(next.dateRange.startDate);

    const isConsecutive = nextStart.diff(currentEnd, 'day') === 1;
    const hasSameRate = areRatesEqual(current.rate, next.rate);

    if (isConsecutive && hasSameRate) {
      // Extend current period
      current.dateRange.endDate = next.dateRange.endDate;
    } else {
      // Periods are not consecutive or have different rates, save current and start new one
      merged.push(current);
      current = { ...next, dateRange: { ...next.dateRange } };
    }
  }

  // Don't forget to add the last period
  merged.push(current);

  return merged;
}

/**
 * Merges utilization data by date, avoiding duplicates.
 */
export function mergeUtilizationByDate(
  existing: DailyUtilizationResponse[],
  newUtilization: DailyUtilizationResponse[]
): DailyUtilizationResponse[] {
  const existingDates = new Set(existing.map(u => u.date));
  const uniqueNew = newUtilization.filter(u => !existingDates.has(u.date));
  return [...existing, ...uniqueNew];
}

/**
 * Merges state changes by effective date range, avoiding duplicates.
 * State changes are uniquely identified by their effectiveStartDate and effectiveEndDate.
 * Returns state changes sorted chronologically by effectiveStartDate (earliest first).
 */
export function mergeStateChanges(
  existing: PlannerUserStateChangeResponse[],
  newStateChanges: PlannerUserStateChangeResponse[]
): PlannerUserStateChangeResponse[] {
  const existingKeys = new Set(
    existing.map(sc => `${sc.effectiveStartDate}-${sc.effectiveEndDate ?? 'null'}`)
  );
  const uniqueNew = newStateChanges.filter(
    sc => !existingKeys.has(`${sc.effectiveStartDate}-${sc.effectiveEndDate ?? 'null'}`)
  );

  const merged = [...existing, ...uniqueNew];
  merged.sort((a, b) => {
    return new Date(a.effectiveStartDate).getTime() - new Date(b.effectiveStartDate).getTime();
  });

  return merged;
}

// ============================================================================
// CROSS-GROUP MEMBER DATA BACKFILL
// ============================================================================

/**
 * Backfills missing assignments, utilization, and state changes for members
 * that appear in multiple groups (departments or projects).
 *
 * When horizontal pagination loads a new date range, the backend may return
 * a member only under the group they belonged to at that time (e.g., user
 * changed departments). The frontend already shows the member under all their
 * groups, but the newly loaded data is missing for groups where the backend
 * didn't return them.
 *
 * This function detects such cases and copies missing data across groups:
 * - Assignments are copied with `belongsToTheGroup = false` (renders dimmed)
 * - Utilization entries are copied as-is for correct metrics
 * - State changes are merged for correct capacity/hours rendering
 *
 * @param groupMembersMap - Map of groupKey → Map of userId → member data.
 *   Mutates members in place (safe because they were already deep-copied).
 * @returns Map of groupKey → Set of utilization date strings that were backfilled
 *   into that group's members but NOT present in the group-level utilization.
 *   Used to show "no data" indicators on group utilization cells.
 */
export function backfillCrossGroupMemberData(
  groupMembersMap: Map<string | number, Map<number, PlannerMemberResponse>>
): Map<string | number, Set<string>> {
  const backfilledDatesPerGroup = new Map<string | number, Set<string>>();

  // Step 1: Build a lookup of userId → all groups containing that user
  const userToGroups = new Map<
    number,
    Array<{ groupKey: string | number; member: PlannerMemberResponse }>
  >();

  groupMembersMap.forEach((membersById, groupKey) => {
    membersById.forEach((member, userId) => {
      let groups = userToGroups.get(userId);
      if (!groups) {
        groups = [];
        userToGroups.set(userId, groups);
      }
      groups.push({ groupKey, member });
    });
  });

  // Step 2: For each user in 2+ groups, backfill missing data
  userToGroups.forEach(groups => {
    if (groups.length < 2) return;

    // Collect the union of all assignment IDs across all groups for this user
    const allAssignmentsById = new Map<number, PlannerAssignmentResponse>();
    groups.forEach(({ member }) => {
      member.assignments.forEach(a => {
        // Keep the first occurrence — if the same assignment exists in multiple
        // groups, the one that `belongsToTheGroup=true` is authoritative
        if (!allAssignmentsById.has(a.assignment.id)) {
          allAssignmentsById.set(a.assignment.id, a);
        }
      });
    });

    // Collect the union of all utilization entries
    const allUtilizationByDate = new Map<string, DailyUtilizationResponse>();
    groups.forEach(({ member }) => {
      member.utilizationByDate.forEach(u => {
        if (!allUtilizationByDate.has(u.date)) {
          allUtilizationByDate.set(u.date, u);
        }
      });
    });

    // Collect the union of all state changes
    let allStateChanges: PlannerUserStateChangeResponse[] = [];
    groups.forEach(({ member }) => {
      allStateChanges = mergeStateChanges(allStateChanges, member.stateChanges);
    });

    // Backfill each group's member with missing data from other groups
    groups.forEach(({ groupKey, member }) => {
      // Backfill missing assignments
      const existingAssignmentIds = new Set(member.assignments.map(a => a.assignment.id));
      allAssignmentsById.forEach((sourceAssignment, assignmentId) => {
        if (existingAssignmentIds.has(assignmentId)) return;

        // Deep copy the assignment and mark as not belonging to this group
        const copy: PlannerAssignmentResponse = {
          ...sourceAssignment,
          assignment: { ...sourceAssignment.assignment },
          activePeriods: [...sourceAssignment.activePeriods],
          overlapsAssignmentIds: [...sourceAssignment.overlapsAssignmentIds],
          belongsToTheGroup: false,
        };
        member.assignments.push(copy);
      });

      // Track which utilization dates are being backfilled into this group's member
      const existingUtilDates = new Set(member.utilizationByDate.map(u => u.date));
      allUtilizationByDate.forEach((_, date) => {
        if (!existingUtilDates.has(date)) {
          let dates = backfilledDatesPerGroup.get(groupKey);
          if (!dates) {
            dates = new Set();
            backfilledDatesPerGroup.set(groupKey, dates);
          }
          dates.add(date);
        }
      });

      // Backfill missing utilization entries
      member.utilizationByDate = mergeUtilizationByDate(
        member.utilizationByDate,
        Array.from(allUtilizationByDate.values())
      );

      // Backfill missing state changes
      member.stateChanges = mergeStateChanges(member.stateChanges, allStateChanges);
    });
  });

  return backfilledDatesPerGroup;
}

// ============================================================================
// VISIBLE DATE RELATION
// ============================================================================

export enum DateRelation {
  Within = 'within',
  Before = 'before',
  After = 'after',
  NearStart = 'near-start',
  NearEnd = 'near-end',
  NoData = 'no-data',
}

/**
 * Determines where a date sits relative to the loaded (queryDateRange) range.
 * 'near-start' / 'near-end' mean the date is within the range but close to an
 * edge (within HORIZONTAL_PAGINATION_THRESHOLD_DAYS), so more data should be
 * pre-fetched — matching the same threshold used by the scroll handler.
 */
export function getDateRelationToLoadedRange(
  date: Dayjs,
  queryDateRange: { startDate: string; endDate: string } | null
): DateRelation {
  if (!queryDateRange) return DateRelation.NoData;

  const rangeStart = dayjs(queryDateRange.startDate);
  const rangeEnd = dayjs(queryDateRange.endDate);

  if (date.isBefore(rangeStart, 'day')) return DateRelation.Before;
  if (date.isAfter(rangeEnd, 'day')) return DateRelation.After;

  // Check proximity to edges — trigger pre-fetching when within the same
  // threshold that the scroll handler uses (HORIZONTAL_PAGINATION_THRESHOLD_DAYS)
  const daysFromStart = date.diff(rangeStart, 'day');
  const daysFromEnd = rangeEnd.diff(date, 'day');

  if (daysFromStart < HORIZONTAL_PAGINATION_THRESHOLD_DAYS) return DateRelation.NearStart;
  if (daysFromEnd < HORIZONTAL_PAGINATION_THRESHOLD_DAYS) return DateRelation.NearEnd;

  return DateRelation.Within;
}

// Intentionally left empty — logDateRelation removed, inline logs used instead

// ============================================================================
// DATE RANGE UTILITIES
// ============================================================================

/**
 * Finds the latest end date across all pages.
 */
export function findLatestEndDate<T extends PageResponseWithMetadata<any>>(pages: T[]): Dayjs {
  return pages.reduce((latest, page) => {
    const pageEndDate = dayjs(page._dateRange.endDate);
    return pageEndDate.isAfter(latest) ? pageEndDate : latest;
  }, dayjs(0));
}

/**
 * Finds the earliest start date across all pages.
 */
export function findEarliestStartDate<T extends PageResponseWithMetadata<any>>(pages: T[]): Dayjs {
  return pages.reduce((earliest, page) => {
    const pageStartDate = dayjs(page._dateRange.startDate);
    return pageStartDate.isBefore(earliest) ? pageStartDate : earliest;
  }, dayjs());
}

/**
 * Merges page date-ranges into maximal CONTIGUOUS intervals.
 *
 * `pruneDistantPages` always keeps the initial-range pages (needed for cursor
 * extraction) even when the user has scrolled far away, so the cache can hold
 * disjoint page ranges with a pruned gap between them. The global min/max
 * envelope hides that gap, which makes the relation check report `Within` for a
 * date that actually has no loaded page — leaving blank cells with no refetch.
 *
 * This merges ranges that touch or overlap (allowing a 1-day adjacency, since
 * consecutive fetches abut on day boundaries) so callers can reason about real
 * coverage instead of the misleading envelope.
 */
export function getContiguousLoadedRanges<
  T extends { _dateRange?: { startDate: string; endDate: string } },
>(pages: T[]): { startDate: Dayjs; endDate: Dayjs }[] {
  const intervals = pages
    .filter((p): p is T & { _dateRange: { startDate: string; endDate: string } } =>
      Boolean(p._dateRange)
    )
    .map(p => ({ start: dayjs(p._dateRange.startDate), end: dayjs(p._dateRange.endDate) }))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  if (intervals.length === 0) return [];

  const merged: { startDate: Dayjs; endDate: Dayjs }[] = [
    { startDate: intervals[0].start, endDate: intervals[0].end },
  ];

  for (let i = 1; i < intervals.length; i++) {
    const last = merged[merged.length - 1];
    const cur = intervals[i];
    // Touching or overlapping (gap of at most 1 day) → extend the current run.
    if (cur.start.diff(last.endDate, 'day') <= 1) {
      if (cur.end.isAfter(last.endDate)) last.endDate = cur.end;
    } else {
      merged.push({ startDate: cur.start, endDate: cur.end });
    }
  }

  return merged;
}

/**
 * Returns the contiguous loaded interval that contains `date`, or null if the
 * date falls in a pruned gap or outside all loaded pages. Used so the loaded
 * range reported to the scroll/relation logic reflects real coverage around the
 * viewport rather than a disjoint min/max envelope.
 */
export function getContiguousRangeContaining<
  T extends { _dateRange?: { startDate: string; endDate: string } },
>(pages: T[], date: Dayjs): { startDate: string; endDate: string } | null {
  for (const interval of getContiguousLoadedRanges(pages)) {
    if (!date.isBefore(interval.startDate, 'day') && !date.isAfter(interval.endDate, 'day')) {
      return {
        startDate: interval.startDate.format(DATE_KEY_FORMAT),
        endDate: interval.endDate.format(DATE_KEY_FORMAT),
      };
    }
  }
  return null;
}

/**
 * Computes the range to report to the scroll/relation logic as the "loaded
 * range" for a given visible date.
 *
 * - If the visible date sits inside a contiguous run, report that run.
 * - If it sits in a pruned interior gap (or outside all runs), report the
 *   NEAREST run instead of the global min/max envelope. This is the fix for the
 *   "scroll far then back → data vanishes" bug: the envelope contains the gap,
 *   so the relation check reads `Within` and never refetches; reporting the
 *   nearest run instead makes the visible date read as `Before`/`After` that
 *   run, which triggers a bridge fetch that progressively refills the gap as the
 *   user scrolls through it.
 *
 * Returns null only when there are no loaded pages at all (caller falls back to
 * the envelope, which is harmless because there is nothing to mask).
 */
export function getReportedRangeForVisibleDate<T extends PageResponseWithMetadata<any>>(
  pages: T[],
  date: Dayjs
): { startDate: string; endDate: string } | null {
  const runs = getContiguousLoadedRanges(pages);
  if (runs.length === 0) return null;

  // Inside a run → report it (relation will be Within/NearStart/NearEnd).
  const containing = runs.find(
    r => !date.isBefore(r.startDate, 'day') && !date.isAfter(r.endDate, 'day')
  );
  const chosen =
    containing ??
    // In a gap (or outside all runs) → pick the run with the smallest distance
    // to the visible date. Distance is 0 inside a run, else the gap in days to
    // the nearest edge. The visible date is guaranteed to be strictly outside
    // the chosen run, so the relation reads Before (run to the right) or After
    // (run to the left) → a bridge fetch fires.
    runs.reduce((nearest, r) => {
      const distR = date.isBefore(r.startDate, 'day')
        ? r.startDate.diff(date, 'day')
        : date.diff(r.endDate, 'day');
      const distN = date.isBefore(nearest.startDate, 'day')
        ? nearest.startDate.diff(date, 'day')
        : date.diff(nearest.endDate, 'day');
      return distR < distN ? r : nearest;
    });

  return {
    startDate: chosen.startDate.format(DATE_KEY_FORMAT),
    endDate: chosen.endDate.format(DATE_KEY_FORMAT),
  };
}

/**
 * Calculates the new date range for future pagination (right scroll).
 * Loads 3 months ahead.
 */
function calculateFutureDateRange(pages: PageResponseWithMetadata<any>[]): DateRangeCalculation {
  const latestEndDate = findLatestEndDate(pages);
  const startDate = latestEndDate.add(1, 'day');
  const endDate = startDate.add(QUARTER_IN_MONTHS, 'month');
  return { startDate, endDate };
}

/**
 * Calculates the new date range for past pagination (left scroll).
 * Loads 3 months before.
 */
function calculatePastDateRange(pages: PageResponseWithMetadata<any>[]): DateRangeCalculation {
  const earliestStartDate = findEarliestStartDate(pages);
  const endDate = earliestStartDate.subtract(1, 'day');
  const startDate = endDate.subtract(QUARTER_IN_MONTHS, 'month');
  return { startDate, endDate };
}

// ============================================================================
// QUERY DATA UTILITIES
// ============================================================================

/**
 * Retrieves and validates raw query data.
 * Returns null if data is invalid.
 */
function getRawQueryData<TResponse extends PageResponseWithMetadata<any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[]
): InfiniteData<TResponse, DualAxisPageParam> | null {
  const rawData = queryClient.getQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey);

  if (!rawData) {
    // Debug: check what keys exist in the cache
    const allQueries = queryClient.getQueryCache().getAll();
    const matchingQueries = allQueries.filter(q => {
      const key = q.queryKey;
      return (
        Array.isArray(key) &&
        key.length > 0 &&
        typeof key[0] === 'string' &&
        key[0].startsWith('assignments-by-')
      );
    });
    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'getRawQueryData: NO DATA for key[0]=' +
        (queryKey[0] as string) +
        ' cacheHas=' +
        matchingQueries
          .map(q => (q.queryKey[0] as string) + '(state=' + q.state.status + ')')
          .join(', ')
    );
  }

  return rawData?.pages.length ? rawData : null;
}

/**
 * Counts total loaded items from vertical/initial pages (not horizontal extensions).
 * Used to determine the dynamic pageSize for horizontal extensions so that
 * new date ranges fetch the same number of rows the user has already scrolled
 * through vertically — preventing empty rows when combining both axes.
 *
 * Vertical pages are identified by their pageParam: they have direction=undefined
 * (initial load or vertical scroll), while horizontal extensions have direction='past'/'future'.
 */
function getLoadedItemCount<TResponse extends PageResponseWithMetadata<any>>(
  pages: TResponse[],
  pageParams: DualAxisPageParam[],
  getItemWeight?: (item: any) => number
): number {
  if (pages.length === 0) return DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS;

  const weightFn = getItemWeight ?? (() => 1);
  let totalWeight = 0;
  for (let i = 0; i < pages.length; i++) {
    const pp = pageParams[i];
    // Vertical/initial pages have no direction; horizontal extensions have 'past'/'future'
    if (!pp?.direction || pp.axis === 'vertical') {
      for (const item of pages[i].items) {
        totalWeight += weightFn(item);
      }
    }
  }

  return Math.max(totalWeight, DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS);
}

/**
 * Updates the planner store's queryDateRanges[groupBy] to reflect the expanded range.
 * Also extends the dates array if the new range goes beyond the current dates,
 * preventing blank cells when backend data outpaces the UI date cells.
 */
function updateStoreDateRange<TResponse extends PageResponseWithMetadata<any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[]
): void {
  const updatedData =
    queryClient.getQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey);

  if (updatedData?.pages.length) {
    const { groupBy, queryDateRanges, firstVisibleDate } = usePlannerStore.getState();
    const oldRange = queryDateRanges[groupBy];

    // Report the contiguous run that covers the visible date — NOT the global
    // min/max envelope. After pruning, the cache can hold disjoint page ranges
    // (the initial range is always kept for cursor extraction), so the envelope
    // can span a pruned interior gap. If we reported the envelope, the relation
    // check would read `Within` for a date that has no loaded page → no bridge
    // fetch → blank cells (the "scroll far then back → data vanishes" bug).
    //
    // When the visible date is in a gap, getReportedRangeForVisibleDate returns
    // the NEAREST run, so the date reads as Before/After it and a bridge fetch
    // fires that progressively refills the gap as the user scrolls through it.
    const reportedRange = firstVisibleDate
      ? getReportedRangeForVisibleDate(updatedData.pages, firstVisibleDate)
      : null;

    const envelope = {
      startDate: findEarliestStartDate(updatedData.pages).format(DATE_KEY_FORMAT),
      endDate: findLatestEndDate(updatedData.pages).format(DATE_KEY_FORMAT),
    };

    const newRange = reportedRange ?? envelope;

    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'updateStoreDateRange: group=' +
        groupBy +
        ' old=' +
        oldRange?.startDate +
        '..' +
        oldRange?.endDate +
        ' new=' +
        newRange.startDate +
        '..' +
        newRange.endDate +
        ' pages=' +
        updatedData.pages.length
    );

    // The dates array is the UI scroll window — managed exclusively by loadDatesLeft/Right
    // and navigation actions (prevDateClick/nextDateClick/setToday). Never extend it here:
    // any direct mutation bypasses the rolling window logic and causes scroll position jumps.
    usePlannerStore.setState({
      queryDateRanges: {
        ...queryDateRanges,
        [groupBy]: newRange,
      },
    });
  }
}

// ============================================================================
// API CALL UTILITIES
// ============================================================================

/**
 * Builds API request parameters for a given cursor.
 */
function buildApiRequestParams(
  cursor: number | null | undefined,
  startDate: string,
  endDate: string,
  filters: FilterValues['values'],
  plannerPreferences: PlannerPreferences,
  pageSize: number = DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS
) {
  const apiFilters = mapAssignmentFilters({ startDate, endDate, filters });
  const utilizationBasedOnDTO = mapUtilizationBasedOnToDTO(plannerPreferences.utilizationBasedOn);
  const utilizationFiltersDTO = mapUtilizationFiltersToDTO(plannerPreferences);

  return {
    filters: apiFilters,
    utilizationBasedOn: utilizationBasedOnDTO,
    utilizationFilters: utilizationFiltersDTO,
    ignorePlaceholdersForGroup: plannerPreferences.ignorePlaceholdersForGroup,
    pagination: {
      cursor: cursor ?? undefined,
      pageSize,
    },
  };
}

/**
 * Creates a page parameter for dual-axis pagination.
 */
function createPageParam(
  startDate: string,
  endDate: string,
  cursor: number | null | undefined,
  direction: PlannerDateRangeDirection
): DualAxisPageParam {
  return {
    startDate,
    endDate,
    cursor,
    direction,
    axis: 'horizontal',
  };
}

/**
 * Creates a response page with metadata.
 */
function createResponsePage<TResponse, TApiResponse>(
  response: TApiResponse,
  startDate: string,
  endDate: string,
  cursor: number | null | undefined
): TResponse {
  return {
    ...response,
    _dateRange: { startDate, endDate },
    _cursor: cursor,
  } as unknown as TResponse;
}

/**
 * Fetches all pages for a new date range using the given cursors.
 */
async function fetchPagesForDateRange<
  TResponse extends PageResponseWithMetadata<any>,
  TApiResponse = Omit<TResponse, '_dateRange' | '_cursor'>,
>(
  cursors: (number | null | undefined)[],
  dateRange: DateRangeCalculation,
  direction: PlannerDateRangeDirection,
  params: FetchDateRangeParams<TApiResponse>,
  pageSize?: number
): Promise<{ pages: TResponse[]; pageParams: DualAxisPageParam[] }> {
  const { apiFn, filters, plannerPreferences } = params;
  const pages: TResponse[] = [];
  const pageParams: DualAxisPageParam[] = [];

  const startDate = dateRange.startDate.format(DATE_KEY_FORMAT);
  const endDate = dateRange.endDate.format(DATE_KEY_FORMAT);

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'fetchPagesForDateRange: BACKEND CALL START ' +
      startDate +
      '..' +
      endDate +
      ' direction=' +
      direction +
      ' cursors=' +
      cursors.length +
      ' pageSize=' +
      (pageSize ?? 'default')
  );

  for (const cursor of cursors) {
    const pageParam = createPageParam(startDate, endDate, cursor, direction);
    const requestParams = buildApiRequestParams(
      cursor,
      startDate,
      endDate,
      filters,
      plannerPreferences,
      pageSize
    );

    const response = await apiFn(requestParams);
    const newPage = createResponsePage<TResponse, TApiResponse>(
      response,
      startDate,
      endDate,
      cursor
    );

    pages.push(newPage);
    pageParams.push(pageParam);
  }

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'fetchPagesForDateRange: BACKEND CALL END ' +
      startDate +
      '..' +
      endDate +
      ' fetched=' +
      pages.length
  );

  return { pages, pageParams };
}

// ============================================================================
// CACHE UPDATE UTILITIES
// ============================================================================

/**
 * Updates query cache by appending new pages (for future dates).
 */
function appendPagesToCache<TResponse extends PageResponseWithMetadata<any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  newPages: TResponse[],
  newPageParams: DualAxisPageParam[]
): void {
  queryClient.setQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey, oldData => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      pages: [...oldData.pages, ...newPages],
      pageParams: [...oldData.pageParams, ...newPageParams],
    };
  });
}

/**
 * Replaces all query cache pages (for large gap scenarios where old data is irrelevant).
 */
function replaceCachePages<TResponse extends PageResponseWithMetadata<any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  newPages: TResponse[],
  newPageParams: DualAxisPageParam[]
): void {
  queryClient.setQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey, oldData => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      pages: newPages,
      pageParams: newPageParams,
    };
  });
}

/**
 * Updates query cache by prepending new pages (for past dates).
 */
function prependPagesToCache<TResponse extends PageResponseWithMetadata<any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  newPages: TResponse[],
  newPageParams: DualAxisPageParam[]
): void {
  queryClient.setQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey, oldData => {
    if (!oldData) return oldData;

    return {
      ...oldData,
      pages: [...newPages, ...oldData.pages],
      pageParams: [...newPageParams, ...oldData.pageParams],
    };
  });
}

// ============================================================================
// VISIBLE-DATE-CENTRIC PAGINATION
// ============================================================================

/**
 * Gap threshold in months: if the visible date is farther than this from the
 * loaded range edge, replace all cache instead of fetching a connecting range.
 */
const GAP_REPLACE_THRESHOLD_MONTHS = 6;

/**
 * Fetches data centered around the visible date when the user has navigated
 * outside the loaded range (e.g. via prev/next arrow buttons).
 *
 * - Small gap (≤6 months): fetches a connecting range that bridges the gap
 *   to the existing data, then prepends/appends.
 * - Large gap (>6 months): fetches a fresh ±3-month window around the visible
 *   date and replaces all cache pages (old data is too far away to be useful).
 */
async function fetchDateRangeAroundVisibleDate<
  TResponse extends PageResponseWithMetadata<any>,
  TApiResponse = Omit<TResponse, '_dateRange' | '_cursor'>,
>(
  params: FetchDateRangeParams<TApiResponse>,
  visibleDate: Dayjs,
  queryDateRange: { startDate: string; endDate: string },
  relation: DateRelation.Before | DateRelation.After
): Promise<void> {
  const { queryClient, queryKey } = params;
  const rawData = getRawQueryData<TResponse>(queryClient, queryKey);
  if (!rawData) {
    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeAroundVisibleDate: no rawData, aborting'
    );
    return;
  }

  const cursors: (number | null | undefined)[] = [undefined];
  const rangeStart = dayjs(queryDateRange.startDate);
  const rangeEnd = dayjs(queryDateRange.endDate);

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'fetchDateRangeAroundVisibleDate: START visibleDate=' +
      visibleDate.format('YYYY-MM-DD') +
      ' relation=' +
      relation +
      ' range=' +
      queryDateRange.startDate +
      '..' +
      queryDateRange.endDate
  );

  let dateRange: DateRangeCalculation;
  let direction: PlannerDateRangeDirection;
  let shouldReplace = false;

  if (relation === DateRelation.Before) {
    const gapMonths = rangeStart.diff(visibleDate, 'month');
    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeAroundVisibleDate: before, gapMonths=' + gapMonths
    );
    if (gapMonths > GAP_REPLACE_THRESHOLD_MONTHS) {
      // Large gap — fresh window around visible date
      dateRange = {
        startDate: visibleDate.subtract(QUARTER_IN_MONTHS, 'month'),
        endDate: visibleDate.add(QUARTER_IN_MONTHS, 'month'),
      };
      shouldReplace = true;
    } else {
      // Small gap — fetch connecting range from (visibleDate - 3mo) to (rangeStart - 1 day)
      dateRange = {
        startDate: visibleDate.subtract(QUARTER_IN_MONTHS, 'month'),
        endDate: rangeStart.subtract(1, 'day'),
      };
    }
    direction = PlannerDateRangeDirection.Past;
  } else {
    const gapMonths = visibleDate.diff(rangeEnd, 'month');
    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeAroundVisibleDate: after, gapMonths=' + gapMonths
    );
    if (gapMonths > GAP_REPLACE_THRESHOLD_MONTHS) {
      // Large gap — fresh window around visible date
      dateRange = {
        startDate: visibleDate.subtract(QUARTER_IN_MONTHS, 'month'),
        endDate: visibleDate.add(QUARTER_IN_MONTHS, 'month'),
      };
      shouldReplace = true;
    } else {
      // Small gap — fetch connecting range from (rangeEnd + 1 day) to (visibleDate + 3mo)
      dateRange = {
        startDate: rangeEnd.add(1, 'day'),
        endDate: visibleDate.add(QUARTER_IN_MONTHS, 'month'),
      };
    }
    direction = PlannerDateRangeDirection.Future;
  }

  // Use default page size when replacing cache (fresh start), otherwise match
  // the vertical depth so the new date range has data for all loaded rows
  const loadedPageSize = shouldReplace
    ? DEFAULT_PAGE_SIZE_PLANNER_ASSIGNMENTS
    : getLoadedItemCount(rawData.pages, rawData.pageParams, params.getItemWeight);

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'fetchDateRangeAroundVisibleDate: fetching ' +
      dateRange.startDate.format('YYYY-MM-DD') +
      '..' +
      dateRange.endDate.format('YYYY-MM-DD') +
      ' direction=' +
      direction +
      ' shouldReplace=' +
      shouldReplace +
      ' pageSize=' +
      loadedPageSize
  );

  const { pages, pageParams } = await fetchPagesForDateRange<TResponse, TApiResponse>(
    cursors,
    dateRange,
    direction,
    params,
    loadedPageSize
  );

  const cacheAction = shouldReplace
    ? 'REPLACE'
    : relation === DateRelation.Before
      ? 'PREPEND'
      : 'APPEND';
  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'fetchDateRangeAroundVisibleDate: fetched ' +
      pages.length +
      ' pages, items=' +
      pages.map(p => p.items.length).join(',') +
      ' cacheAction=' +
      cacheAction
  );

  if (shouldReplace) {
    replaceCachePages<TResponse>(queryClient, queryKey, pages, pageParams);
  } else if (relation === DateRelation.Before) {
    prependPagesToCache(queryClient, queryKey, pages, pageParams);
  } else {
    appendPagesToCache(queryClient, queryKey, pages, pageParams);
  }

  updateStoreDateRange<TResponse>(queryClient, queryKey);

  const { groupBy: currentGroupBy, queryDateRanges: updatedRanges } = usePlannerStore.getState();
  const updatedRange = updatedRanges[currentGroupBy];
  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'fetchDateRangeAroundVisibleDate: END updatedRange=' +
      updatedRange?.startDate +
      '..' +
      updatedRange?.endDate
  );
}

/**
 * Removes pages whose date range midpoint is too far from the visible date.
 * Always preserves pages matching the initial range (pages[0]._dateRange)
 * because getLoadedItemCount depends on them for dynamic page size calculation.
 */
function pruneDistantPages<TResponse extends PageResponseWithMetadata<any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  visibleDate: Dayjs
): void {
  const data = queryClient.getQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey);
  if (!data || data.pages.length <= 1) return;

  const initialRange = data.pages[0]._dateRange;
  const pruneThresholdMonths = MAX_CACHED_DATE_RANGE_MONTHS / 2; // 6 months from visible date

  const indicesToKeep: number[] = [];
  const prunedRanges: string[] = [];

  data.pages.forEach((page, index) => {
    // Always keep initial range pages (needed by extractCursorsFromInitialRange)
    const isInitialRange =
      page._dateRange.startDate === initialRange.startDate &&
      page._dateRange.endDate === initialRange.endDate;

    if (isInitialRange) {
      indicesToKeep.push(index);
      return;
    }

    // Calculate midpoint of this page's date range
    const pageStart = dayjs(page._dateRange.startDate);
    const pageEnd = dayjs(page._dateRange.endDate);
    const pageMidpoint = pageStart.add(pageEnd.diff(pageStart, 'day') / 2, 'day');
    const distanceMonths = Math.abs(visibleDate.diff(pageMidpoint, 'month'));

    if (distanceMonths <= pruneThresholdMonths) {
      indicesToKeep.push(index);
    } else {
      prunedRanges.push(
        `${page._dateRange.startDate} to ${page._dateRange.endDate} (${distanceMonths}mo away)`
      );
    }
  });

  // Only update cache if we actually pruned something
  if (indicesToKeep.length < data.pages.length) {
    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'pruneDistantPages: visibleDate=' +
        visibleDate.format('YYYY-MM-DD') +
        ' total=' +
        data.pages.length +
        ' keeping=' +
        indicesToKeep.length +
        ' pruned=' +
        prunedRanges.join('; ')
    );
    queryClient.setQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey, oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: indicesToKeep.map(i => oldData.pages[i]),
        pageParams: indicesToKeep.map(i => oldData.pageParams[i]),
      };
    });
    updateStoreDateRange<TResponse>(queryClient, queryKey);
  }
}

// ============================================================================
// LOADING STATE UTILITIES
// ============================================================================

/**
 * Sets the loading state and returns a cleanup function.
 */
function withLoadingState(isLoading: boolean): () => void {
  usePlannerStore.getState().setIsLoadingDateRange(isLoading);
  return () => {
    usePlannerStore.getState().setIsLoadingDateRange(false);
  };
}

// ============================================================================
// MAIN PAGINATION FUNCTIONS
// ============================================================================

/**
 * Core function that handles horizontal date range pagination.
 *
 * Before falling back to edge-based extension, checks whether the user's
 * visible date sits outside the currently loaded range (e.g. after arrow
 * navigation).  When it does, delegates to `fetchDateRangeAroundVisibleDate`
 * which fetches data centered on the visible date instead of extending from
 * the cache edge.  After every cache update, distant pages are pruned to
 * prevent unbounded growth.
 */
async function fetchDateRangeCore<
  TResponse extends PageResponseWithMetadata<any>,
  TApiResponse = Omit<TResponse, '_dateRange' | '_cursor'>,
>(
  params: FetchDateRangeParams<TApiResponse>,
  calculateDateRange: (pages: PageResponseWithMetadata<any>[]) => DateRangeCalculation,
  direction: PlannerDateRangeDirection,
  updateCache: (
    queryClient: QueryClient,
    queryKey: readonly unknown[],
    pages: TResponse[],
    pageParams: DualAxisPageParam[]
  ) => void
): Promise<void> {
  const clearLoadingState = withLoadingState(true);
  const { queryClient, queryKey } = params;

  try {
    const rawData = getRawQueryData<TResponse>(queryClient, queryKey);
    if (!rawData) {
      logger.debug(PLANNER_PAGINATION_LOG_TAG, 'fetchDateRangeCore: no rawData in cache, aborting');
      return;
    }

    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeCore: START direction=' + direction + ' pagesInCache=' + rawData.pages.length
    );

    // Check if the visible date is outside or near the edge of the loaded range
    const { firstVisibleDate, queryDateRanges, groupBy } = usePlannerStore.getState();
    const queryDateRange = queryDateRanges[groupBy];

    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeCore: firstVisibleDate=' +
        firstVisibleDate?.format('YYYY-MM-DD') +
        ' queryDateRange=' +
        queryDateRange?.startDate +
        '..' +
        queryDateRange?.endDate
    );

    if (firstVisibleDate && queryDateRange) {
      // Check the edge relevant to the fetch direction:
      // - For past fetches, check firstVisibleDate (left viewport edge)
      // - For future fetches, check the last visible date (right viewport edge)
      const { visibleDateRange, dates: storeDates } = usePlannerStore.getState();
      const relevantDate =
        direction === PlannerDateRangeDirection.Future &&
        visibleDateRange.endIndex > 0 &&
        storeDates.length > 0
          ? storeDates[Math.min(visibleDateRange.endIndex, storeDates.length - 1)]
          : firstVisibleDate;

      const relation = getDateRelationToLoadedRange(relevantDate, queryDateRange);
      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'fetchDateRangeCore: relevantDate=' +
          relevantDate.format('YYYY-MM-DD') +
          ' relation=' +
          relation +
          ' direction=' +
          direction
      );

      if (relation === DateRelation.Before || relation === DateRelation.After) {
        // Relevant viewport edge is outside loaded range → fetch around visible date
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          'fetchDateRangeCore: relevant edge OUTSIDE range, delegating to fetchDateRangeAroundVisibleDate'
        );
        await fetchDateRangeAroundVisibleDate<TResponse, TApiResponse>(
          params,
          firstVisibleDate,
          queryDateRange,
          relation
        );
        pruneDistantPages<TResponse>(queryClient, queryKey, firstVisibleDate);
        return;
      }

      if (relation === DateRelation.Within) {
        // Both viewport edges have plenty of buffer — skip to prevent redundant fetches
        logger.debug(
          PLANNER_PAGINATION_LOG_TAG,
          'fetchDateRangeCore: relevant edge WITHIN range, skipping'
        );
        return;
      }

      // near-start or near-end: fall through to standard edge extension
      logger.debug(
        PLANNER_PAGINATION_LOG_TAG,
        'fetchDateRangeCore: relation=' + relation + ' — edge extension'
      );
    }

    // Standard edge-based extension (for gradual manual scrolling or near-edge)
    // Use a single API call with a larger pageSize that matches the total number of
    // vertically loaded items, so the new date range has data for all visible rows.
    const cursors: (number | null | undefined)[] = [undefined];
    const loadedPageSize = getLoadedItemCount(
      rawData.pages,
      rawData.pageParams,
      params.getItemWeight
    );

    // Anchor the extension to the contiguous run the VIEWPORT is in, not the
    // global min/max of all pages. After pruning, the cache can hold a far-away
    // disconnected island (e.g. the original initial-range pages kept for cursor
    // extraction). Extending from the global edge would fetch a chunk adjacent to
    // that island — miles from the viewport — which `pruneDistantPages` then
    // deletes, leaving the near-edge condition still true. That is a fetch→prune→
    // fetch loop that marches through empty history forever and hammers the API.
    // Extending from the viewport's own run keeps the fetched chunk adjacent to
    // it, so the pruner keeps it and the run grows past the near-edge threshold,
    // which clears the condition and stops the loop.
    const viewportRun = firstVisibleDate
      ? getContiguousRangeContaining(rawData.pages, firstVisibleDate)
      : null;
    const dateRange = viewportRun
      ? direction === PlannerDateRangeDirection.Past
        ? {
            startDate: dayjs(viewportRun.startDate)
              .subtract(1, 'day')
              .subtract(QUARTER_IN_MONTHS, 'month'),
            endDate: dayjs(viewportRun.startDate).subtract(1, 'day'),
          }
        : {
            startDate: dayjs(viewportRun.endDate).add(1, 'day'),
            endDate: dayjs(viewportRun.endDate).add(1, 'day').add(QUARTER_IN_MONTHS, 'month'),
          }
      : calculateDateRange(rawData.pages);

    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeCore: edge extension fetchStart=' +
        dateRange.startDate.format('YYYY-MM-DD') +
        ' fetchEnd=' +
        dateRange.endDate.format('YYYY-MM-DD') +
        ' anchoredToViewportRun=' +
        Boolean(viewportRun) +
        ' pageSize=' +
        loadedPageSize
    );

    const { pages, pageParams } = await fetchPagesForDateRange<TResponse, TApiResponse>(
      cursors,
      dateRange,
      direction,
      params,
      loadedPageSize
    );

    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'fetchDateRangeCore: fetched ' +
        pages.length +
        ' pages, items=' +
        pages.map(p => p.items.length).join(',')
    );

    updateCache(queryClient, queryKey, pages, pageParams);
    updateStoreDateRange<TResponse>(queryClient, queryKey);

    // Prune pages that are too far from the visible date to keep cache bounded (~12 months max).
    // Without this, gradual edge scrolling would accumulate pages indefinitely.
    if (firstVisibleDate) {
      pruneDistantPages<TResponse>(queryClient, queryKey, firstVisibleDate);
    }
  } finally {
    clearLoadingState();
    logger.debug(PLANNER_PAGINATION_LOG_TAG, 'fetchDateRangeCore: END');
  }
}

/**
 * Fetches future date range (right scroll) for horizontal pagination.
 * Fetches all cursor pages that were loaded for the initial date range.
 */
export async function fetchNextDateRange<
  TResponse extends PageResponseWithMetadata<any>,
  TApiResponse = Omit<TResponse, '_dateRange' | '_cursor'>,
>(params: FetchDateRangeParams<TApiResponse>): Promise<void> {
  await fetchDateRangeCore(
    params,
    calculateFutureDateRange,
    PlannerDateRangeDirection.Future,
    appendPagesToCache
  );
}

/**
 * Refreshes a grouped query (by-department or by-project) as a single backend request
 * after an assignment mutation. Instead of invalidating each vertical page individually
 * (which triggers N backend calls for N loaded pages), this function:
 * 1. Reads the current loaded item count from the cache (sum of all vertical pages)
 * 2. Makes a single API call with that consolidated pageSize
 * 3. Replaces only the vertical pages in the cache (horizontal extension pages are preserved)
 *
 * This is the post-mutation equivalent of horizontal-pagination's "dynamic page size" pattern.
 *
 * @param queryClient - TanStack Query client
 * @param queryKey - Full query key of the grouped query (includes filters and utilization params)
 * @param apiFn - API function to call for the refetch
 * @param getItemWeight - Optional function to weight items for pageSize calculation (e.g. member count for dept/project)
 * @param dateRangeOverride - Optional explicit range to refresh instead of the full
 *   envelope across all pages. Used by the periodic background refresh to fetch only
 *   the contiguous run the viewport is in (the 6–12 months actually in memory around
 *   the viewport), rather than the global min/max — which, after far scrolling, spans
 *   a pruned gap between a disconnected far island and the viewport run (e.g. 27 months).
 *   When omitted, the post-mutation behavior is unchanged (refresh the whole envelope).
 */
export async function refreshGroupedQueryAsSingleRequest<
  TResponse extends PageResponseWithMetadata<any>,
  TApiResponse = Omit<TResponse, '_dateRange' | '_cursor'>,
>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  apiFn: (params: any) => Promise<TApiResponse>,
  getItemWeight?: (item: any) => number,
  dateRangeOverride?: { startDate: string; endDate: string }
): Promise<void> {
  const rawData = getRawQueryData<TResponse>(queryClient, queryKey);
  if (!rawData) {
    logger.debug(
      PLANNER_PAGINATION_LOG_TAG,
      'refreshGroupedQueryAsSingleRequest: no rawData for key[0]=' +
        (queryKey[0] as string) +
        ', aborting'
    );
    return;
  }

  // Extract filters and planner preferences from the query key
  // Query key structure: [prefix, filters, { utilizationBasedOn, assignmentStatuses }]
  // Note: showFilteredOutAssignments is intentionally excluded — it is a UI-only preference
  // and must not trigger a data reload when changed.
  const filters = queryKey[1] as FilterValues['values'];
  const plannerPreferences = queryKey[2] as PlannerPreferences;

  // Refresh the explicit override range when given (background refresh: viewport's
  // loaded run only), else the full envelope across all cached pages (post-mutation).
  const startDate =
    dateRangeOverride?.startDate ?? findEarliestStartDate(rawData.pages).format(DATE_KEY_FORMAT);
  const endDate =
    dateRangeOverride?.endDate ?? findLatestEndDate(rawData.pages).format(DATE_KEY_FORMAT);

  // Count how many items are currently loaded across all vertical pages
  const loadedPageSize = getLoadedItemCount(rawData.pages, rawData.pageParams, getItemWeight);

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'refreshGroupedQueryAsSingleRequest: key[0]=' +
      (queryKey[0] as string) +
      ' dateRange=' +
      startDate +
      '..' +
      endDate +
      ' override=' +
      Boolean(dateRangeOverride) +
      ' pageSize=' +
      loadedPageSize +
      ' (was ' +
      rawData.pages.length +
      ' pages)'
  );

  const requestParams = buildApiRequestParams(
    undefined,
    startDate,
    endDate,
    filters,
    plannerPreferences,
    loadedPageSize
  );

  const response = await apiFn(requestParams);

  // Build a single page that replaces all vertical pages
  // Use the initial page's param structure (direction=undefined, axis='horizontal')
  // so getLoadedItemCount and getNextPageParam treat it correctly as a vertical/initial page
  const initialPageParam = rawData.pageParams[0];
  const newPage = createResponsePage<TResponse, TApiResponse>(
    response,
    startDate,
    endDate,
    undefined
  );

  // Preserve horizontal extension pages (direction='past'/'future'), replace vertical pages
  const horizontalPages: TResponse[] = [];
  const horizontalPageParams: DualAxisPageParam[] = [];
  for (let i = 0; i < rawData.pages.length; i++) {
    const pp = rawData.pageParams[i];
    if (pp?.direction === 'past' || pp?.direction === 'future') {
      horizontalPages.push(rawData.pages[i]);
      horizontalPageParams.push(pp);
    }
  }

  queryClient.setQueryData<InfiniteData<TResponse, DualAxisPageParam>>(queryKey, oldData => {
    if (!oldData) return oldData;
    return {
      ...oldData,
      pages: [newPage, ...horizontalPages],
      pageParams: [initialPageParam, ...horizontalPageParams],
    };
  });

  logger.debug(
    PLANNER_PAGINATION_LOG_TAG,
    'refreshGroupedQueryAsSingleRequest: done, key[0]=' +
      (queryKey[0] as string) +
      ' preservedHorizontalPages=' +
      horizontalPages.length
  );
}

/**
 * Fetches past date range (left scroll) for horizontal pagination.
 * Fetches all cursor pages that were loaded for the initial date range.
 */
export async function fetchPreviousDateRange<
  TResponse extends PageResponseWithMetadata<any>,
  TApiResponse = Omit<TResponse, '_dateRange' | '_cursor'>,
>(params: FetchDateRangeParams<TApiResponse>): Promise<void> {
  await fetchDateRangeCore(
    params,
    calculatePastDateRange,
    PlannerDateRangeDirection.Past,
    prependPagesToCache
  );
}
